import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { Box, Paper, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { fetchAPI } from '../../../api/request';

const stringifyValue = (value) => {
  if (value == null || value === '') {
    return '-';
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const capitalizeLabel = (label) => {
  if (!label) {
    return '';
  }

  const normalized = String(label).replace(/_/g, ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const itemCategoriesApi = {
  path: '/item-categories?pagination[page]=1&pagination[pageSize]=1000',
  method: 'GET',
  get: null,
  auth: true,
  fallback: [],
  transform: (data) =>
    data?.data?.map((item) => ({
      code: item?.attributes?.code,
      description: item?.attributes?.description,
    })) || [],
};

const getRelationLabel = (relation) => {
  const code =
    relation?.code || relation?.attributes?.code || relation?.data?.attributes?.code;
  const description =
    relation?.description || relation?.attributes?.description || relation?.data?.attributes?.description;
  return [code, description].filter(Boolean).join(' - ') || undefined;
};

const formatCategoryValue = (selectedItem, categoryDescriptionByCode) => {
  const relationLabel = getRelationLabel(selectedItem?.item_category);
  if (relationLabel) {
    return relationLabel;
  }

  const categoryCode = selectedItem?.category;
  const fallbackDescription = categoryDescriptionByCode?.[categoryCode];
  if (categoryCode && fallbackDescription) {
    return `${categoryCode} - ${fallbackDescription}`;
  }

  return categoryCode;
};

const Row = ({ label, value }) => (
  <Box display='flex' alignItems='flex-start' gap={2} width='100%'>
    <Typography variant='caption' sx={{ fontWeight: 700, width: '35%' }}>
      {capitalizeLabel(label)}
    </Typography>
    <Typography variant='body2' sx={{ width: '65%' }}>
      {stringifyValue(value)}
    </Typography>
  </Box>
);

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
};

const SelectedItemPreview = ({ watchField = 'id_item', label = 'Product details' }) => {
  const { watch } = useFormContext();
  const { jwt } = useSelector((state) => state.login);
  const selectedItem = watch(watchField);
  const [categoryDescriptionByCode, setCategoryDescriptionByCode] = useState({});

  useEffect(() => {
    let isMounted = true;

    fetchAPI(itemCategoriesApi, {}, {}, {}, jwt)
      .then((categories) => {
        if (!isMounted) {
          return;
        }

        const byCode = (categories || []).reduce((acc, item) => {
          if (item?.code) {
            acc[item.code] = item.description || '';
          }
          return acc;
        }, {});

        setCategoryDescriptionByCode(byCode);
      })
      .catch(() => {
        if (isMounted) {
          setCategoryDescriptionByCode({});
        }
      });

    return () => {
      isMounted = false;
    };
  }, [jwt]);

  const details = useMemo(() => {
    if (!selectedItem) {
      return null;
    }

    return [
      { label: 'mexal_code', value: selectedItem?.mexal_code },
      { label: 'description', value: selectedItem?.name_ITA },
      { label: 'Alternative Code', value: selectedItem?.item_type_ITA },
      { label: 'Unit', value: selectedItem?.main_unit_measure },
      {
        label: 'Other Unit',
        value: selectedItem?.other_unit_of_measure,
      },
      {
        label: 'statistical_category',
        value: getRelationLabel(selectedItem?.item_statistical_category),
      },
      {
        label: 'merchandise_group',
        value: getRelationLabel(selectedItem?.item_merchandise_group),
      },
      {
        label: 'category',
        value: formatCategoryValue(selectedItem, categoryDescriptionByCode),
      },
      { label: 'vat_rate', value: selectedItem?.vat_rate },
      { label: 'price', value: selectedItem?.price },
      { label: 'existence', value: selectedItem?.existence },
      { label: 'location', value: selectedItem?.location },
      { label: 'sub_location', value: selectedItem?.sub_location },
      { label: 'descriptions_in_languages', value: selectedItem?.name_ENG },
    ];
  }, [selectedItem, categoryDescriptionByCode]);

  const detailsColumns = useMemo(() => {
    if (!details?.length) {
      return [[], []];
    }

    const midpoint = Math.ceil(details.length / 2);
    return [details.slice(0, midpoint), details.slice(midpoint)];
  }, [details]);

  if (!details) {
    return null;
  }

  return (
    <Paper
      variant='outlined'
      sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', width: '100%' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 3,
          rowGap: 1,
        }}
      >
        {detailsColumns.map((column, columnIndex) => (
          <Box key={`column-${columnIndex}`} display='flex' flexDirection='column' gap={0.5}>
            {column.map((field) => (
              <Row key={field.label} label={field.label} value={field.value} />
            ))}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

SelectedItemPreview.propTypes = {
  watchField: PropTypes.string,
  label: PropTypes.string,
};

export default SelectedItemPreview;